'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { Wifi, WifiOff, RefreshCw } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

type ConnectionState = 'offline' | 'syncing' | 'live';

export default function ConnectionStatus() {
  const [state, setState] = useState<ConnectionState>('syncing');
  const [visible, setVisible] = useState(false);

  const updateState = useCallback((online: boolean, realtimeConnected: boolean) => {
    if (!online) {
      setState('offline');
      setVisible(true);
    } else if (!realtimeConnected) {
      setState('syncing');
      setVisible(true);
    } else {
      setState('live');
      // Hide the "live" badge after 3 seconds — only show it briefly when transitioning back online
      setTimeout(() => setVisible(false), 3000);
    }
  }, []);

  useEffect(() => {
    const supabase = createClient();
    let realtimeConnected = false;

    // Track online/offline
    const handleOnline = () => updateState(true, realtimeConnected);
    const handleOffline = () => updateState(false, realtimeConnected);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Track Supabase realtime channel status
    const channel = supabase.channel('connection-probe');
    channel
      .on('system', { event: 'connected' }, () => {
        realtimeConnected = true;
        updateState(navigator.onLine, true);
      })
      .on('system', { event: 'disconnected' }, () => {
        realtimeConnected = false;
        updateState(navigator.onLine, false);
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          realtimeConnected = true;
          updateState(navigator.onLine, true);
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          realtimeConnected = false;
          updateState(navigator.onLine, false);
        }
      });

    // Initial state
    if (!navigator.onLine) {
      setState('offline');
      setVisible(true);
    } else {
      setState('syncing');
      setVisible(true);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      supabase.removeChannel(channel);
    };
  }, [updateState]);

  if (!visible) return null;

  const config = {
    offline: {
      icon: WifiOff,
      label: 'Offline',
      className: 'bg-red-900/80 text-red-200 border-red-700/60',
      dotClass: 'bg-red-400',
      animate: false,
    },
    syncing: {
      icon: RefreshCw,
      label: 'Verbinde…',
      className: 'bg-amber-900/80 text-amber-200 border-amber-700/60',
      dotClass: 'bg-amber-400',
      animate: true,
    },
    live: {
      icon: Wifi,
      label: 'Live',
      className: 'bg-green-900/80 text-green-200 border-green-700/60',
      dotClass: 'bg-green-400',
      animate: true,
    },
  }[state];

  const IconComp = config.icon;

  return (
    <div
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-all duration-300 ${config.className}`}
      title={
        state === 'offline' ?'Keine Internetverbindung – App läuft offline'
          : state === 'syncing' ?'Verbindung zu Supabase wird hergestellt…' :'Live-Verbindung aktiv – Änderungen werden sofort angezeigt'
      }
    >
      <span
        className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${config.dotClass} ${
          config.animate ? 'animate-pulse' : ''
        }`}
      />
      <IconComp
        size={11}
        className={`flex-shrink-0 ${state === 'syncing' ? 'animate-spin' : ''}`}
      />
      <span>{config.label}</span>
    </div>
  );
}
